// Port of SuiteStory/Astronomy.swift: sun and moon for a place and moment (Apple spec §8.3: computed locally with
// standard algorithms, so it works offline and for any historical date). NOAA's solar calculator for the sun, Meeus
// (Astronomical Algorithms ch. 48, low precision) for the moon. Accuracy is a minute or two for the sun and a few
// percent for the moon's illumination: plenty for fiction. Instants are milliseconds since 1970 (UTC).
import { offsetSeconds, type Zone } from './zones.ts';

export interface SunDay {
  /** Null when the sun doesn't rise or set that day (polar day or night). */
  sunrise: number | null;
  sunset: number | null;
  solarNoon: number;
}

export interface MoonState {
  /** 0 = new, 1 = full. */
  illumination: number;
  /** Waxing (growing towards full) or waning. */
  waxing: boolean;
  /** Days since the last new moon. */
  age: number;
}

/** `Moon.phaseName`. */
export function phaseName(m: MoonState): string {
  const i = m.illumination;
  if (i < 0.03) return 'new moon';
  if (i < 0.40) return m.waxing ? 'waxing crescent' : 'waning crescent';
  if (i < 0.60) return m.waxing ? 'first quarter' : 'last quarter';
  if (i < 0.97) return m.waxing ? 'waxing gibbous' : 'waning gibbous';
  return 'full moon';
}

// MARK: Time

const seconds = (ms: number) => ms / 1000;
const julianDay = (ms: number) => seconds(ms) / 86400 + 2440587.5;
const radians = (d: number) => (d * Math.PI) / 180;
const degrees = (r: number) => (r * 180) / Math.PI;
/** Swift's `truncatingRemainder(dividingBy:)` (the sign of the dividend), which is JS `%`. */
const trem = (x: number, y: number) => x % y;

/** `utcOffset(at:longitude:timeZone:)`: seconds from UTC at a place — its zone's, or local mean time from its longitude
 *  (the clock before standard time zones). */
export function utcOffset(ms: number, longitude: number, zone: Zone | null): number {
  if (zone) return offsetSeconds(zone, ms);
  return (longitude / 15) * 3600;
}

const utcMidnight = (s: number) => Math.floor(s / 86400) * 86400;

/** The sun's declination (radians) and the equation of time (minutes), at `s` seconds since 1970. */
function sun(s: number): [number, number] {
  const t = (s / 86400 + 2440587.5 - 2451545) / 36525;
  const l0 = trem(280.46646 + t * (36000.76983 + t * 0.0003032), 360);
  const m = 357.52911 + t * (35999.05029 - 0.0001537 * t);
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
  const c = Math.sin(radians(m)) * (1.914602 - t * (0.004817 + 0.000014 * t)) + Math.sin(radians(2 * m)) * (0.019993 - 0.000101 * t)
    + Math.sin(radians(3 * m)) * 0.000289;
  const trueLongitude = l0 + c;
  const omega = 125.04 - 1934.136 * t;
  const lambda = trueLongitude - 0.00569 - 0.00478 * Math.sin(radians(omega));
  const epsilon0 = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const epsilon = epsilon0 + 0.00256 * Math.cos(radians(omega));
  const declination = Math.asin(Math.sin(radians(epsilon)) * Math.sin(radians(lambda)));
  const y = Math.tan(radians(epsilon / 2)) ** 2;
  const eot = 4 * degrees(y * Math.sin(2 * radians(l0)) - 2 * e * Math.sin(radians(m)) + 4 * e * y * Math.sin(radians(m)) * Math.cos(2 * radians(l0))
    - 0.5 * y * y * Math.sin(4 * radians(l0)) - 1.25 * e * e * Math.sin(2 * radians(m)));
  return [declination, eot];
}

/** `Astronomy.day`: sunrise, sunset and solar noon for the local calendar day containing `ms`. */
export function sunDay(ms: number, latitude: number, longitude: number, zone: Zone | null): SunDay {
  const offset = utcOffset(ms, longitude, zone);
  // Local midnight of that day, as a UTC instant (seconds).
  const localSeconds = seconds(ms) + offset;
  const midnight = Math.floor(localSeconds / 86400) * 86400 - offset;
  const event = (hourAngleSign: number): number | null => {
    // Iterate twice: the sun's position at the event's own time.
    let t = midnight + 12 * 3600;
    for (let i = 0; i < 2; i++) {
      const [declination, equationOfTime] = sun(t);
      const noonMinutes = 720 - 4 * longitude - equationOfTime; // UTC minutes after UTC midnight
      if (hourAngleSign === 0) {
        t = utcMidnight(t) + noonMinutes * 60;
        continue;
      }
      const cosH = (Math.cos(radians(90.833)) - Math.sin(radians(latitude)) * Math.sin(declination)) / (Math.cos(radians(latitude)) * Math.cos(declination));
      if (!(cosH >= -1 && cosH <= 1)) return null;
      const h = degrees(Math.acos(cosH));
      t = utcMidnight(t) + (noonMinutes - hourAngleSign * 4 * h) * 60;
    }
    // Keep the event on the requested local day.
    while (t < midnight) t += 86400;
    while (t >= midnight + 86400) t -= 86400;
    return t;
  };
  // Sunset is the one after that day's sunrise, which may be past local midnight (late solar noon, high summer).
  const sunrise = event(1);
  let sunset = event(-1);
  if (sunrise !== null && sunset !== null) {
    let set = sunset;
    while (set <= sunrise) set += 86400;
    while (set > sunrise + 86400) set -= 86400;
    sunset = set;
  }
  const noon = event(0) ?? midnight + 43200;
  const toMs = (s: number | null) => (s === null ? null : s * 1000);
  return { sunrise: toMs(sunrise), sunset: toMs(sunset), solarNoon: noon * 1000 };
}

/** The sun's geometric elevation above the horizon, in degrees. */
export function sunElevation(ms: number, latitude: number, longitude: number): number {
  const s = seconds(ms);
  const [declination, equationOfTime] = sun(s);
  const utcMinutes = (s - utcMidnight(s)) / 60;
  const hourAngle = radians((utcMinutes + equationOfTime + 4 * longitude) / 4 - 180);
  const cosZenith = Math.sin(radians(latitude)) * Math.sin(declination) + Math.cos(radians(latitude)) * Math.cos(declination) * Math.cos(hourAngle);
  return 90 - degrees(Math.acos(Math.min(1, Math.max(-1, cosZenith))));
}

/** `isDaylight`: the sun's upper edge above the horizon, allowing for refraction (the −0.833° sunrise and sunset
 *  use). Works through polar day and night and sunsets after midnight. Null for a place without finite coordinates. */
export function isDaylight(ms: number, latitude: number, longitude: number): boolean | null {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return sunElevation(ms, latitude, longitude) > -0.833;
}

/** `Astronomy.moon`. */
export function moon(ms: number): MoonState {
  const t = (julianDay(ms) - 2451545) / 36525;
  const d = radians(trem(297.8501921 + 445267.1114034 * t, 360)); // mean elongation
  const m = radians(trem(357.5291092 + 35999.0502909 * t, 360)); // sun's anomaly
  const mp = radians(trem(134.9633964 + 477198.8675055 * t, 360)); // moon's anomaly
  // Phase angle i (degrees): 180 − D with the main periodic terms.
  const i = 180 - degrees(d) - 6.289 * Math.sin(mp) + 2.1 * Math.sin(m) - 1.274 * Math.sin(2 * d - mp) - 0.658 * Math.sin(2 * d)
    - 0.214 * Math.sin(2 * mp) - 0.11 * Math.sin(d);
  const illumination = (1 + Math.cos(radians(i))) / 2;
  let elongation = trem(degrees(d), 360);
  if (elongation < 0) elongation += 360;
  return { illumination, waxing: elongation < 180, age: (elongation / 360) * 29.530588 };
}
